import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../../lib/utils';

/** The kit's one hover delay. */
const TOOLTIP_DELAY_MS = 300;

/** True beneath an app-level TooltipProvider, so a Tooltip does not shadow it. */
const TooltipProviderPresent = React.createContext(false);

/**
 * Mounted once, in App. Sharing one provider is what lets the pointer move
 * between two adjacent row tooltips and open the second one without waiting
 * out the delay again. Pages never mount their own.
 */
const TooltipProvider: React.FC<React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>> = ({
  delayDuration = TOOLTIP_DELAY_MS,
  ...props
}) => (
  <TooltipProviderPresent.Provider value>
    <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />
  </TooltipProviderPresent.Provider>
);

/**
 * Carries its own provider when none is mounted above it (a page test, the
 * design-system page), and defers to App's shared one when it is — so no
 * page or test needs a TooltipProvider wrapper.
 */
const Tooltip: React.FC<React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>> = (props) => {
  const hasProvider = React.useContext(TooltipProviderPresent);
  if (hasProvider) return <TooltipPrimitive.Root {...props} />;
  return (
    <TooltipPrimitive.Provider delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipPrimitive.Root {...props} />
    </TooltipPrimitive.Provider>
  );
};

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-md bg-foreground px-3 py-1.5 text-xs text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
